import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Navigate, useSearchParams } from "react-router-dom"

import Icon from "@/components/Icon"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import VehicleAssignmentTab from "@/components/vehicles/VehicleAssignmentTab.tsx"
import VehicleDetail from "@/components/vehicles/VehicleDetail"
import VehicleDirectory from "@/components/vehicles/VehicleDirectory"
import { useDepots } from "@/hooks/useDepots"
import { KEYLESS_DASHBOARD, useHasFeatureAccess } from "@/hooks/useFeatureFlags"
import { type VehicleListRow, useVehicles } from "@/hooks/useVehicles"
import DashboardLayout from "@/layouts/DashboardLayout"

import { OPERATIONAL_STATUSES, USABLE_OPERATIONAL_STATUSES } from "../../shared/vehicle.ts"

export default function Vehicles() {
  const hasFeatureAccess = useHasFeatureAccess(KEYLESS_DASHBOARD)

  const [search, setSearch] = useState("")
  const [depotId, setDepotId] = useState<string>("all")
  const [vehicleGroup, setVehicleGroup] = useState<string>("all")
  const [keylessOnly, setKeylessOnly] = useState(false)
  const [statuses, setStatuses] = useState<string[]>(USABLE_OPERATIONAL_STATUSES)

  const [searchParams, setSearchParams] = useSearchParams()
  const selectedVehicleId = searchParams.get("vehicleId")
  const activeTab = searchParams.get("tab") === "assignment" ? "assignment" : "directory"
  const dateParam = searchParams.get("date")
  const selectedDate = useMemo(() => {
    const parsed = dateParam ? new Date(`${dateParam}T00:00:00`) : new Date()
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed
  }, [dateParam])

  const setParam = (key: string, value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(key, value)
        return next
      },
      { replace: true },
    )

  const panelRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const { data: depots } = useDepots()

  // A fresh object here would be a fresh query key on every render, so the list would come back
  // with a new identity and restart the directory's paging.
  const filters = useMemo(
    () => ({
      depotId: depotId === "all" ? undefined : depotId,
      vehicleGroup: vehicleGroup === "all" ? undefined : vehicleGroup,
      keylessOnly,
      operationalStatuses: statuses,
      search,
    }),
    [depotId, vehicleGroup, keylessOnly, statuses, search],
  )

  const { data: vehicles, isLoading, error } = useVehicles(filters)

  // The panel reads the row the list already loaded, so a vehicleId outside the current filters
  // has nothing to show until the filters are cleared.
  const selectedVehicle = useMemo(
    () => (vehicles ?? []).find((r) => r.vehicle.id === selectedVehicleId) ?? null,
    [vehicles, selectedVehicleId],
  )

  const vehicleGroups = useMemo(() => {
    const groups = new Set((vehicles ?? []).map((r) => r.vehicle.vehicle_group).filter(Boolean))
    return [...groups].sort()
  }, [vehicles])

  const selectVehicle = useCallback(
    (vehicle: VehicleListRow | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (vehicle) {
            next.set("vehicleId", vehicle.vehicle.id)
          } else {
            next.delete("vehicleId")
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  useEffect(() => {
    if (!selectedVehicleId) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        contentRef.current?.contains(target)
      ) {
        selectVehicle(null)
      }
    }
    document.addEventListener("click", handleClick)
    return () => document.removeEventListener("click", handleClick)
  }, [selectedVehicleId, selectVehicle])

  // Team access is the route guard's; this is the flag half. Undefined means unresolved, so hold -
  // redirecting there bounces the user on a hard refresh.
  if (hasFeatureAccess === undefined) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
          <span className="ml-2 text-palette-jungleGreen700">Loading vehicles...</span>
        </div>
      </DashboardLayout>
    )
  }

  if (!hasFeatureAccess) {
    return <Navigate to="/no-permission" replace />
  }

  return (
    <DashboardLayout>
      <Tabs value={activeTab} onValueChange={(value) => setParam("tab", value)}>
        <TabsList className="mb-4">
          <TabsTrigger value="directory" className="flex items-center gap-2">
            <Icon name="truck" className="h-4 w-4 text-current" />
            Vehicles
          </TabsTrigger>
          <TabsTrigger value="assignment" className="flex items-center gap-2">
            <Icon name="calendarBlank" className="h-4 w-4 text-current" />
            Assign vehicles
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assignment">
          <VehicleAssignmentTab
            selectedDate={selectedDate}
            onSelectedDateChange={(date) =>
              setParam(
                "date",
                `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`,
              )
            }
          />
        </TabsContent>

        <TabsContent value="directory">
          <div
            ref={contentRef}
            className="flex h-[calc(100vh-theme(spacing.6)-theme(spacing.6)-64px)] flex-col"
          >
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                <Spinner size="lg" />
                <span className="ml-2 text-palette-jungleGreen700">Loading vehicles...</span>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <Icon name="warningCircleFilled" className="h-4 w-4" />
                <AlertDescription>
                  Error loading vehicles: {error instanceof Error ? error.message : "Unknown error"}
                </AlertDescription>
              </Alert>
            )}

            <div className="pb-4">
              <h2 className="mb-3 text-lg font-semibold text-palette-jungleGreen900">Vehicles</h2>

              <Card>
                <CardContent className="flex flex-wrap items-end gap-4 p-4">
                  <div className="min-w-[240px] flex-1 space-y-2">
                    <Label htmlFor="vehicle-search">Search</Label>
                    <div className="relative">
                      <Icon
                        name="magnifyingGlass"
                        className="text-muted-foreground absolute left-2 top-2.5 h-4 w-4"
                      />
                      <Input
                        id="vehicle-search"
                        className="pl-8"
                        placeholder="Search registration or driver"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="depot-filter">Depot</Label>
                    <Select value={depotId} onValueChange={setDepotId}>
                      <SelectTrigger id="depot-filter" className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={"all"}>All depots</SelectItem>
                        {(depots ?? []).map((depot) => (
                          <SelectItem key={depot.id} value={depot.id}>
                            {depot.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="group-filter">Zerity group</Label>
                    <Select value={vehicleGroup} onValueChange={setVehicleGroup}>
                      <SelectTrigger id="group-filter" className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={"all"}>All groups</SelectItem>
                        {vehicleGroups.map((group) => (
                          <SelectItem key={group} value={group}>
                            {group}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="min-w-[220px] space-y-2">
                    <Label>Vehicle status</Label>
                    <MultiSelect
                      options={OPERATIONAL_STATUSES.map((s) => ({ value: s, label: s }))}
                      selected={statuses}
                      onChange={setStatuses}
                      placeholder="Vehicle status"
                    />
                  </div>

                  <div className="flex items-center gap-2 pb-2">
                    <Switch
                      id="keyless-only"
                      checked={keylessOnly}
                      onCheckedChange={setKeylessOnly}
                    />
                    <Label htmlFor="keyless-only">Keyless only</Label>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex flex-1 overflow-hidden rounded-lg bg-white shadow-sm">
              <div
                className={`flex flex-col overflow-hidden transition-all ${
                  selectedVehicleId ? "w-[40%]" : "w-full"
                }`}
              >
                <VehicleDirectory
                  vehicles={vehicles ?? []}
                  selectedVehicleId={selectedVehicleId}
                  onSelectVehicle={selectVehicle}
                />
              </div>

              {selectedVehicleId && (
                <div
                  ref={panelRef}
                  className="w-[60%] overflow-auto shadow-[-4px_0_12px_-2px_rgba(0,0,0,0.08)]"
                >
                  {selectedVehicle ? (
                    <VehicleDetail row={selectedVehicle} onClose={() => selectVehicle(null)} />
                  ) : (
                    !isLoading && (
                      <Alert className="m-4">
                        <Icon name="warningCircleFilled" className="h-4 w-4" />
                        <AlertDescription>
                          This vehicle is not in the current view. Clear the filters to see it.
                        </AlertDescription>
                      </Alert>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  )
}
